import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  Gift,
  Package,
  MessageCircle,
  PackageX,
  Search,
  ShoppingCart,
  Store,
  X,
} from "lucide-react";
import { api, money } from "@/lib/api";
import { cn } from "@/lib/utils";
import ShopFooter from "@/components/ShopFooter";

/**
 * A client's public shop.
 *
 * No sign-in, and no data beyond what the server chooses to send. The colour
 * comes from the client's chosen preset and is applied as a CSS variable, so
 * one value themes the whole page.
 */
export default function Shop() {
  const { slug } = useParams();

  // A finger or pointer landing on a card starts its bigger photo downloading,
  // so it's usually there by the time the card opens.
  useEffect(() => {
    const warm = (e) => prefetchPhoto(e.target.closest?.("[data-large]")?.dataset.large);
    document.addEventListener("pointerover", warm, { passive: true });
    document.addEventListener("touchstart", warm, { passive: true });
    document.addEventListener("focusin", warm);
    return () => {
      document.removeEventListener("pointerover", warm);
      document.removeEventListener("touchstart", warm);
      document.removeEventListener("focusin", warm);
    };
  }, []);
  // Always fetched fresh. A shop shows live prices and availability, so a
  // remembered copy would quietly go stale.
  const [shop, setShop] = useState(null);
  const [state, setState] = useState("loading");
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState(null);
  // A ready-made gift box someone tapped, to show what's inside — kept apart
  // from `chosen` since a box isn't an item and opens a different sheet.
  const [chosenBox, setChosenBox] = useState(null);
  // Departments are doorways, not shortcuts sitting above an
  // already-visible catalogue: with departments on the shop, nothing is
  // listed until a customer walks into one, searches, or explicitly asks to
  // see everything. A shop with no departments set up is unaffected — it
  // keeps showing the plain catalogue as it always has.
  const [browsingAll, setBrowsingAll] = useState(false);
  // Which department a customer has walked into, if any. Null means they're
  // still at the doorways (or never had any to walk through).
  const [activeDeptId, setActiveDeptId] = useState(null);
  // The chip chosen inside a department — a department's own sub-filter,
  // separate from the flat catalogue's category dropdown.
  const [deptCategory, setDeptCategory] = useState("all");

  // Loose items, and any number of boxes each with its own name. Kept in
  // memory only — nothing is reserved or paid for, so there's no order to
  // persist.
  const [basket, setBasket] = useState([]);
  const [boxes, setBoxes] = useState([]);
  // Which box "Put in the box" adds to. Null means nobody's building one.
  const [buildingId, setBuildingId] = useState(null);
  // false, or which side of the bag is showing.
  const [bagOpen, setBagOpen] = useState(false);
  // So a category card can jump straight to the filtered grid below it.
  const finderRef = useRef(null);

  useEffect(() => {
    // Reached at /shop/<slug> on our domain, or on the client's own address —
    // in which case the hostname is what identifies them.
    api
      .get(slug ? `/shop/${slug}` : "/shop-by-host")
      .then(({ data }) => {
        setShop(data);
        // An address that belongs to a client whose shop is switched off.
        setState(data.closed ? "closed" : "ready");
        document.title = data.headline || data.name || document.title;
      })
      .catch(() => setState("missing"));
  }, [slug]);

  const visible = useMemo(() => {
    // This runs on every render, before the early returns below — and a closed
    // shop has no items at all, so guard on the list rather than the object.
    if (!shop?.items) return [];
    const q = query.trim().toLowerCase();
    return shop.items.filter(
      (i) =>
        (category === "all" || i.category === category) &&
        (!q ||
          i.name.toLowerCase().includes(q) ||
          (i.description || "").toLowerCase().includes(q))
    );
  }, [shop, category, query]);

  const activeDept = useMemo(
    () => shop?.departments?.find((d) => d.id === activeDeptId) || null,
    [shop, activeDeptId]
  );

  const deptVisible = useMemo(() => {
    if (!activeDept || activeDept.kind !== "categories") return [];
    return shop.items.filter(
      (i) =>
        activeDept.categories.some((c) => c.name === i.category) &&
        (deptCategory === "all" || i.category === deptCategory)
    );
  }, [shop, activeDept, deptCategory]);

  if (state === "loading") return <div className="min-h-screen bg-surface" />;

  if (state === "closed") {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <div className="w-12 h-12 rounded-2xl bg-brand-light flex items-center justify-center mx-auto mb-4">
            <Store className="w-5 h-5 text-brand" />
          </div>
          <h1 className="font-display text-xl font-semibold text-ink">{shop?.name}</h1>
          <p className="mt-2 text-[13.5px] text-muted2 leading-relaxed">
            The shop isn't open at the moment. Do check back.
          </p>
        </div>
      </div>
    );
  }

  if (state === "missing") {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <div className="w-12 h-12 rounded-2xl bg-brand-light flex items-center justify-center mx-auto mb-4">
            <Store className="w-5 h-5 text-brand" />
          </div>
          <h1 className="font-display text-xl font-semibold text-ink">Nothing here</h1>
          <p className="mt-2 text-[13.5px] text-muted2 leading-relaxed">
            This shop isn't open, or the address is wrong.
          </p>
        </div>
      </div>
    );
  }

  const sym = shop.currency_symbol;
  // wa.me won't accept spaces or dashes.
  const wa = (shop.whatsapp || "").replace(/\D/g, "");

  // A box can be "any 4 to 6", so there's a floor to reach and a ceiling to stop at.
  // Whatever this client calls them — box, hamper, bundle.
  const word = shop.box_word || "box";
  const Word = word.charAt(0).toUpperCase() + word.slice(1);

  const boxMin = shop.box_min || shop.box_size || 5;
  const boxMax = Math.max(shop.box_max || shop.box_size || 5, boxMin);

  const building = boxes.find((b) => b.id === buildingId) || null;
  const basketCount = basket.reduce((n, b) => n + b.qty, 0);
  const boxCount = boxes.length;

  const boxTotal = (box) =>
    shop.box_price || box.lines.reduce((n, l) => n + (l.price || 0), 0);
  const basketTotal =
    basket.reduce((n, b) => n + (b.kind === "box" ? b.price : (b.price || 0) * b.qty), 0);

  /** Items eligible for a box, if the client limited it. */
  const boxAllows = (item) =>
    !shop.box_categories?.length || shop.box_categories.includes(item.category);

  const hasDepts = shop.departments?.length > 0;
  // With departments on the shop, the flat catalogue only appears once a
  // customer has actually asked to see it — by the dropdown, a search, or
  // the explicit "browse everything" link. Without departments, it's always
  // shown, so this never changes anything for a shop that hasn't set any up.
  const showCatalogue = !activeDept && (browsingAll || query.trim() !== "" || !hasDepts);

  /** Walking through a doorway. */
  const enterDepartment = (dept) => {
    setActiveDeptId(dept.id);
    setDeptCategory("all");
    setBrowsingAll(false);
    setQuery("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /** Stepping back out to the doorways. */
  const backToDoorway = () => {
    setActiveDeptId(null);
    setBrowsingAll(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /** The doorway search box, or its dropdown's "browse everything" — either
   *  one skips straight past the doorways to the flat catalogue. */
  const chooseCategory = (name) => {
    setActiveDeptId(null);
    setBrowsingAll(true);
    setCategory(name);
  };

  const startBox = () => {
    const id = `box-${Date.now()}`;
    // Named so several can be told apart — "Mum's box", "for Kay".
    // Named so several can be told apart — "Mum's box", "for Kay".
    setBoxes((cur) => [...cur, { id, name: `${Word} ${cur.length + 1}`, lines: [] }]);
    setBuildingId(id);
  };

  const addToBox = (item, delta = 1) => {
    if (!buildingId) return;
    setBoxes((cur) =>
      cur.map((box) => {
        if (box.id !== buildingId) return box;
        const has = box.lines.find((l) => l.name === item.name);
        if (delta < 0) {
          return { ...box, lines: box.lines.filter((l) => l.name !== item.name) };
        }
        if (has || box.lines.length >= boxMax) return box;
        return { ...box, lines: [...box.lines, item] };
      })
    );
  };

  const addToBasket = (item, delta = 1) => {
    setBasket((cur) => {
      const found = cur.find((b) => b.name === item.name);
      if (!found) return delta > 0 ? [...cur, { ...item, qty: 1 }] : cur;
      const qty = found.qty + delta;
      return qty <= 0
        ? cur.filter((b) => b.name !== item.name)
        : cur.map((b) => (b.name === item.name ? { ...b, qty } : b));
    });
  };

  /** Fold a finished box into the basket as one line with its own price. */
  const boxToBasket = (box) => {
    setBasket((cur) => [
      ...cur,
      { kind: "box", id: box.id, name: box.name, price: boxTotal(box), qty: 1, lines: box.lines },
    ]);
    setBoxes((cur) => cur.filter((b) => b.id !== box.id));
    if (buildingId === box.id) setBuildingId(null);
  };

  /** Taking a box out of the basket puts it back as a box, not as loose items. */
  const basketBoxOut = (line) => {
    setBasket((cur) => cur.filter((b) => b.id !== line.id));
    setBoxes((cur) => [...cur, { id: line.id, name: line.name, lines: line.lines }]);
  };

  const describeBox = (box) => {
    const lines = box.lines
      .map((l) => `· ${l.name}${l.code ? ` [${l.code}]` : ""}`)
      .join("\n");
    const price = shop.box_price ? ` — ${money(shop.box_price, sym)}` : "";
    return `${box.name} (${box.lines.length} items)${price}:\n${lines}`;
  };

  const boxLink = (box) =>
    `https://wa.me/${wa}?text=${encodeURIComponent(
      `Hi ${shop.name} — I'd like a build-your-own ${word}.\n\n${describeBox(box)}\n\nCan you make this up?`
    )}`;

  /** Everything in the basket as one message, boxes written out in full. */
  const basketLink = () => {
    const lines = basket
      .map((b) =>
        b.kind === "box"
          ? describeBox(b)
          : `${b.qty} × ${b.name}${b.code ? ` [${b.code}]` : ""}${
              b.price ? ` — ${money(b.price * b.qty, sym)}` : ""
            }`
      )
      .join("\n");
    const total = basketTotal ? `\n\nTotal ${money(basketTotal, sym)}.` : "";
    return `https://wa.me/${wa}?text=${encodeURIComponent(
      `Hi ${shop.name} — I'd like:\n${lines}${total} Is that available?`
    )}`;
  };

  /** A message with the item already named, so an order arrives complete. */
  const askAbout = (item) => {
    const price = item.price !== undefined ? ` (${money(item.price, sym)})` : "";
    const ref = item.code ? ` [${item.code}]` : "";
    const text = `Hi ${shop.name} — I'd like the ${item.name}${ref}${price}. Is it available?`;
    return `https://wa.me/${wa}?text=${encodeURIComponent(text)}`;
  };

  return (
    <div className="min-h-screen bg-surface flex flex-col" style={{ "--shop": shop.colour }}>
      <header className="border-b border-line bg-white sticky top-0 z-20">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-3.5 flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-[10px] flex items-center justify-center shrink-0"
            style={{ background: "var(--shop)" }}
          >
            <span className="font-display text-white text-[15px] font-semibold leading-none">
              {shop.name[0]?.toUpperCase()}
            </span>
          </div>
          <span className="font-display text-[16px] font-semibold text-ink truncate">
            {shop.name}
          </span>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            {shop.allow_box && boxCount > 0 && (
              <button
                onClick={() => setBagOpen("box")}
                aria-label={`${boxCount} box${boxCount === 1 ? "" : "es"}`}
                className="relative inline-flex items-center justify-center w-9 h-9 rounded-full border border-gold text-gold bg-gold-light transition-colors"
              >
                <Package className="w-[18px] h-[18px]" />
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-gold text-white text-[10.5px] font-mono flex items-center justify-center">
                  {boxCount}
                </span>
              </button>
            )}
            {shop.allow_cart && (
              // Always present, so people know it's there before they've put
              // anything in it.
              <button
                onClick={() => basketCount > 0 && setBagOpen("basket")}
                aria-label={`Basket, ${basketCount} item${basketCount === 1 ? "" : "s"}`}
                className={cn(
                  "relative inline-flex items-center justify-center w-9 h-9 rounded-full border transition-colors",
                  basketCount > 0
                    ? "border-line hover:border-muted2"
                    : "border-line/70 text-muted2 cursor-default"
                )}
              >
                <ShoppingCart className="w-[18px] h-[18px]" />
                {basketCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10.5px] font-mono flex items-center justify-center"
                    style={{ background: "var(--shop)" }}
                  >
                    {basketCount}
                  </span>
                )}
              </button>
            )}
            {wa && (
              <a
                href={`https://wa.me/${wa}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-stock-in text-white text-[13px] font-medium hover:opacity-90 transition-opacity"
              >
                <MessageCircle className="w-4 h-4" />
                <span className="hidden sm:inline">Order on WhatsApp</span>
                <span className="sm:hidden">Order</span>
              </a>
            )}
          </div>
        </div>
      </header>

      <div
        style={{
          background:
            "linear-gradient(160deg, color-mix(in srgb, var(--shop) 9%, white) 0%, #FAF9F8 70%)",
        }}
      >
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-12 sm:py-16 text-center">
          <h1 className="font-display text-[28px] sm:text-[36px] leading-[1.13] font-semibold tracking-[-0.022em] text-ink max-w-[17ch] mx-auto">
            {shop.headline}
          </h1>
          {shop.tagline && (
            <p className="mt-4 text-[15px] text-muted2 max-w-[46ch] mx-auto leading-relaxed">
              {shop.tagline}
            </p>
          )}
          {shop.tags?.length > 0 && (
            <div className="flex gap-2 justify-center flex-wrap mt-5">
              {shop.tags.map((t) => (
                <span
                  key={t}
                  className="bg-white/80 border border-line rounded-full px-3.5 py-1.5 text-[12px] text-muted2"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <main className="flex-1">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 pb-16">

          {!activeDept && !showCatalogue && (
            <>
              <DepartmentCards shop={shop} onPick={enterDepartment} />

              <div ref={finderRef} className="scroll-mt-20">
                <Finder
                  shop={shop}
                  sym={sym}
                  query={query}
                  onQuery={(q) => {
                    setQuery(q);
                    if (q.trim()) {
                      setActiveDeptId(null);
                      setBrowsingAll(true);
                    }
                  }}
                  category="all"
                  onCategory={chooseCategory}
                  onOpenItem={setChosen}
                />
              </div>

              <div className="text-center py-14">
                <p className="text-[13px] text-muted2">
                  Pick a department above, or{" "}
                  <button
                    onClick={() => chooseCategory("all")}
                    className="underline underline-offset-2 hover:text-ink"
                  >
                    browse everything
                  </button>
                  .
                </p>
              </div>
            </>
          )}

          {activeDept?.kind === "categories" && (
            <div>
              <BackToDoorway onClick={backToDoorway} />
              <DeptHeader dept={activeDept} />

              <div className="flex gap-2 flex-wrap mb-6">
                <DeptChip
                  active={deptCategory === "all"}
                  onClick={() => setDeptCategory("all")}
                >
                  All {activeDept.name.toLowerCase()}
                </DeptChip>
                {activeDept.categories.map((c) => (
                  <DeptChip
                    key={c.name}
                    active={deptCategory === c.name}
                    onClick={() => setDeptCategory(c.name)}
                  >
                    {c.name} · {c.item_count}
                  </DeptChip>
                ))}
              </div>

              {deptVisible.length === 0 ? (
                <div className="text-center py-20">
                  <PackageX className="w-6 h-6 text-muted2 mx-auto mb-3" />
                  <p className="text-[13.5px] text-muted2">Nothing to show just yet.</p>
                </div>
              ) : (
                <ItemGrid
                  items={deptVisible}
                  shop={shop}
                  sym={sym}
                  building={building}
                  boxAllows={boxAllows}
                  boxFull={!!building && building.lines.length >= boxMax}
                  basket={basket}
                  onOpenItem={setChosen}
                  onBox={addToBox}
                  onBasket={addToBasket}
                />
              )}
            </div>
          )}

          {activeDept?.kind === "giftboxes" && (
            <div>
              <BackToDoorway onClick={backToDoorway} />
              <DeptHeader dept={activeDept} subtitle="Pick one ready-made, or build your own" />

              {activeDept.allow_box && (
                <BuildBanner
                  shop={shop}
                  sym={sym}
                  word={word}
                  boxMin={boxMin}
                  boxMax={boxMax}
                  building={building}
                  boxes={boxes}
                  onStart={startBox}
                  onSeeIt={() => setBagOpen("box")}
                  onDone={() => setBuildingId(null)}
                />
              )}

              {activeDept.giftboxes?.length > 0 && (
                <>
                  <div className="eyebrow mb-3">Ready-made · one of a kind</div>
                  <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-2">
                    {activeDept.giftboxes.map((b) => (
                      <GiftBoxCard
                        key={b.build_id}
                        box={b}
                        sym={sym}
                        shop={shop}
                        wa={wa}
                        onOpen={setChosenBox}
                      />
                    ))}
                  </div>
                </>
              )}

              {building && (
                <>
                  <div className="eyebrow mt-8 mb-3">Pick items for your {word}</div>
                  {shop.items.filter(boxAllows).length === 0 ? (
                    <p className="text-[13px] text-muted2">
                      Nothing eligible is in the shop right now.
                    </p>
                  ) : (
                    <ItemGrid
                      items={shop.items.filter(boxAllows)}
                      shop={shop}
                      sym={sym}
                      building={building}
                      boxAllows={boxAllows}
                      boxFull={!!building && building.lines.length >= boxMax}
                      basket={basket}
                      onOpenItem={setChosen}
                      onBox={addToBox}
                      onBasket={addToBasket}
                    />
                  )}
                </>
              )}

              {!activeDept.giftboxes?.length && !activeDept.allow_box && (
                <p className="text-[13.5px] text-muted2 text-center py-14">
                  Nothing to show just yet.
                </p>
              )}
            </div>
          )}

          {!activeDept && showCatalogue && (
            <>
              {hasDepts && <BackToDoorway onClick={backToDoorway} />}

              {!hasDepts && shop.allow_box && (
                <BuildBanner
                  shop={shop}
                  sym={sym}
                  word={word}
                  boxMin={boxMin}
                  boxMax={boxMax}
                  building={building}
                  boxes={boxes}
                  onStart={startBox}
                  onSeeIt={() => setBagOpen("box")}
                  onDone={() => setBuildingId(null)}
                />
              )}

              <div ref={finderRef} className="scroll-mt-20">
                <Finder
                  shop={shop}
                  sym={sym}
                  query={query}
                  onQuery={setQuery}
                  category={category}
                  onCategory={setCategory}
                  onOpenItem={setChosen}
                />
              </div>

              {visible.length === 0 ? (
                <div className="text-center py-20">
                  <PackageX className="w-6 h-6 text-muted2 mx-auto mb-3" />
                  <p className="text-[13.5px] text-muted2">
                    {query
                      ? "Nothing matches that."
                      : "Nothing to show just yet. Message us and we'll let you know what's coming."}
                  </p>
                </div>
              ) : (
                <ItemGrid
                  items={visible}
                  shop={shop}
                  sym={sym}
                  building={building}
                  boxAllows={boxAllows}
                  boxFull={!!building && building.lines.length >= boxMax}
                  basket={basket}
                  onOpenItem={setChosen}
                  onBox={addToBox}
                  onBasket={addToBasket}
                />
              )}
            </>
          )}
        </div>
      </main>

      <ShopFooter shop={shop} />

      {bagOpen && (
        <BagPanel
          side={bagOpen}
          onSide={setBagOpen}
          shop={shop}
          sym={sym}
          boxes={boxes}
          basket={basket}
          basketTotal={basketTotal}
          boxTotal={boxTotal}
          boxMin={boxMin}
          boxMax={boxMax}
          buildingId={buildingId}
          onBuild={setBuildingId}
          onRename={(id, name) =>
            setBoxes((cur) => cur.map((b) => (b.id === id ? { ...b, name } : b)))
          }
          onDropBox={(id) => {
            setBoxes((cur) => cur.filter((b) => b.id !== id));
            if (buildingId === id) setBuildingId(null);
          }}
          onBoxLineOut={(boxId, line) =>
            setBoxes((cur) =>
              cur.map((b) =>
                b.id === boxId ? { ...b, lines: b.lines.filter((l) => l.name !== line.name) } : b
              )
            )
          }
          onBoxToBasket={boxToBasket}
          onBasketBoxOut={basketBoxOut}
          boxLink={boxLink}
          basketLink={basketLink}
          onAdd={addToBasket}
          onRemove={(item) => setBasket((cur) => cur.filter((b) => b.name !== item.name))}
          onClose={() => setBagOpen(false)}
        />
      )}

      {chosen && (
        <ItemSheet
          item={chosen}
          sym={sym}
          wa={wa}
          askAbout={askAbout}
          onClose={() => setChosen(null)}
        />
      )}

      {chosenBox && (
        <GiftBoxSheet
          box={chosenBox}
          sym={sym}
          wa={wa}
          shop={shop}
          onClose={() => setChosenBox(null)}
        />
      )}
    </div>
  );
}

/**
 * One doorway card per department — the shop's own "walk into a shop"
 * front door. A department only ever appears here once it has something
 * inside it (the server filters that out), so this either renders nothing
 * (nothing set up yet) or a real, populated set of doorways.
 */
function DepartmentCards({ shop, onPick }) {
  if (!shop.departments?.length) return null;
  return (
    <div className="pt-2 pb-6">
      <div className="eyebrow mb-1.5">Shop by</div>
      <h2 className="font-display text-[20px] font-semibold text-ink mb-5">Department</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {shop.departments.map((d) => (
          <button
            key={d.id}
            onClick={() => onPick(d)}
            className="text-left rounded-2xl border border-line bg-white overflow-hidden shadow-card hover:shadow-lift hover:-translate-y-0.5 transition-all"
          >
            <div className="h-[140px] bg-surface overflow-hidden flex items-center justify-center">
              {d.photo_url ? (
                <img src={d.photo_url} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              ) : d.kind === "giftboxes" ? (
                <Gift className="w-7 h-7 text-line" />
              ) : (
                <Store className="w-7 h-7 text-line" />
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="font-display text-[15px] font-semibold text-ink truncate">
                  {d.name}
                </div>
                <span className="shrink-0 font-mono text-[11px] text-muted2">
                  {d.item_count}
                </span>
              </div>
              <span
                className="mt-3 inline-block text-[12px] font-medium px-3 py-1.5 rounded-full text-white"
                style={{ background: "var(--shop)" }}
              >
                Shop {d.name.toLowerCase()}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/** A quiet way back out to the doorways, sitting above a department's own header. */
function BackToDoorway({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-[12.5px] text-muted2 hover:text-ink mb-5 -ml-1 px-1 py-1"
    >
      <ChevronLeft className="w-3.5 h-3.5" />
      All departments
    </button>
  );
}

/** The department's own little banner — its photo (or a fallback icon), name, and count. */
function DeptHeader({ dept, subtitle }) {
  return (
    <div className="flex items-center gap-4 mb-6">
      <div className="w-14 h-14 rounded-2xl bg-surface overflow-hidden shrink-0 flex items-center justify-center border border-line">
        {dept.photo_url ? (
          <img src={dept.photo_url} alt="" className="w-full h-full object-cover" />
        ) : dept.kind === "giftboxes" ? (
          <Gift className="w-6 h-6 text-line" />
        ) : (
          <Store className="w-6 h-6 text-line" />
        )}
      </div>
      <div className="min-w-0">
        <h2 className="font-display text-[22px] font-semibold text-ink truncate">{dept.name}</h2>
        <p className="text-[12.5px] text-muted2 mt-0.5">
          {subtitle || `${dept.item_count} item${dept.item_count === 1 ? "" : "s"}`}
        </p>
      </div>
    </div>
  );
}

/** One filter pill inside a department — same idea as the flat shop's category chips. */
function DeptChip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "px-3.5 py-1.5 rounded-full text-[12.5px] border transition-colors",
        active
          ? "bg-brand-light text-brand border-brand-light font-medium"
          : "border-line text-muted2 hover:border-muted2"
      )}
    >
      {children}
    </button>
  );
}

/** The build-your-own banner — unchanged from the old flat shop, just given a home
 *  of its own so it can sit either above the gift-boxes department or, for a shop
 *  with no departments set up at all, above the plain catalogue as it always did. */
function BuildBanner({ shop, sym, word, boxMin, boxMax, building, boxes, onStart, onSeeIt, onDone }) {
  return (
    <div className="rounded-2xl border border-line bg-white p-4 mb-5 flex items-center gap-4 flex-wrap">
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-medium text-ink">
          {building
            ? `${building.name} · ${building.lines.length} of ${boxLabel(boxMin, boxMax)} chosen`
            : `Build your own ${word}`}
        </div>
        <p className="text-[12.5px] text-muted2 mt-0.5">
          {building
            ? building.lines.length >= boxMin
              ? "That's enough — add more or send it over."
              : `Choose ${boxMin - building.lines.length} more`
            : `Pick any ${boxLabel(boxMin, boxMax)} items${
                shop.box_price ? ` for ${money(shop.box_price, sym)}` : ""
              }.`}
        </p>
      </div>

      {building && (
        <div className="w-full sm:w-[150px] h-1.5 rounded-full bg-surface overflow-hidden order-last sm:order-none">
          <span
            className="block h-full rounded-full bg-gold transition-all"
            style={{ width: `${Math.min(100, (building.lines.length / boxMin) * 100)}%` }}
          />
        </div>
      )}

      <div className="flex gap-2 shrink-0">
        {building ? (
          <>
            <button
              onClick={onSeeIt}
              className="h-9 px-4 rounded-full border border-line text-[12.5px] text-ink hover:border-muted2"
            >
              See it
            </button>
            <button onClick={onDone} className="h-9 px-4 rounded-full text-[12.5px] text-muted2">
              Done
            </button>
          </>
        ) : (
          <button
            onClick={onStart}
            className="h-9 px-4 rounded-full bg-gold text-white text-[12.5px] font-medium hover:opacity-90"
          >
            {boxes.length > 0 ? `Start another ${word}` : `Start a ${word}`}
          </button>
        )}
      </div>
    </div>
  );
}

/** A ready-made box sitting in the gift-boxes department — one of a kind, so
 *  there's no add-to-basket control, just a WhatsApp message ready to send. */
function GiftBoxCard({ box, sym, shop, wa, onOpen }) {
  const link = wa
    ? `https://wa.me/${wa}?text=${encodeURIComponent(
        `Hi ${shop.name} — I'd like the ${box.name}${
          box.price ? ` (${money(box.price, sym)})` : ""
        }. Is it available?`
      )}`
    : null;

  return (
    <div
      data-large={box.photo || undefined}
      className="rounded-2xl border border-line bg-white overflow-hidden flex flex-col"
    >
      <button onClick={() => onOpen(box)} className="text-left">
        <div className="aspect-square w-full bg-surface flex items-center justify-center overflow-hidden">
          {box.photo ? (
            <img src={box.thumb || box.photo} alt={box.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
          ) : (
            <Gift className="w-7 h-7 text-line" />
          )}
        </div>
      </button>
      <div className="p-3.5 flex flex-col flex-1">
        <button
          onClick={() => onOpen(box)}
          className="text-left text-[13px] text-ink leading-snug min-h-[36px]"
        >
          {box.name}
        </button>
        {box.price !== undefined && box.price !== null && (
          <span className="font-semibold text-[14px] text-ink mt-2">{money(box.price, sym)}</span>
        )}
        {/* A hint that the card itself opens, same as an item's name/photo do —
            without this, nothing on a box's face suggests it's tappable. */}
        <button
          onClick={() => onOpen(box)}
          className="mt-1.5 flex items-center gap-1 text-[11px] text-muted2 hover:text-ink"
        >
          <Search className="w-2.5 h-2.5" />
          See what's inside
        </button>
        {link && (
          <a
            href={link}
            target="_blank"
            rel="noreferrer"
            className="mt-2 flex items-center justify-center gap-1.5 rounded-lg py-2 text-[12px] font-medium bg-gold-light text-gold hover:opacity-90 transition-opacity"
          >
            <MessageCircle className="w-3.5 h-3.5" />
            Ask about this
          </a>
        )}
      </div>
    </div>
  );
}

/**
 * Stop the page behind a popup from scrolling. Hiding the scrollbar makes the
 * page wider by its width (about 15px on Windows), which shifted everything to
 * the right; padding by exactly that much keeps the page where it was. Phones
 * and Macs have no visible scrollbar, so there the padding is zero.
 */
function lockPageScroll() {
  const body = document.body;
  const gap = window.innerWidth - document.documentElement.clientWidth;
  const before = { overflow: body.style.overflow, paddingRight: body.style.paddingRight };
  body.style.overflow = "hidden";
  if (gap > 0) body.style.paddingRight = `${gap}px`;
  return () => {
    body.style.overflow = before.overflow;
    body.style.paddingRight = before.paddingRight;
  };
}

/** A ready-made box, opened — its photo (or a fallback icon) and what's
 *  actually inside it, the same shape of sheet an item gets. Never its cost. */
function GiftBoxSheet({ box, sym, wa, shop, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    const unlock = lockPageScroll();
    return () => {
      window.removeEventListener("keydown", onKey);
      unlock();
    };
  }, [onClose]);

  const link = wa
    ? `https://wa.me/${wa}?text=${encodeURIComponent(
        `Hi ${shop.name} — I'd like the ${box.name}${
          box.price ? ` (${money(box.price, sym)})` : ""
        }. Is it available?`
      )}`
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <button onClick={onClose} aria-label="Close" className="absolute inset-0 bg-ink/40" />
      <div className="relative bg-white w-full sm:max-w-[720px] sm:rounded-2xl rounded-t-2xl overflow-hidden max-h-[92vh] overflow-y-auto">
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 z-10 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center"
        >
          <X className="w-4 h-4 text-ink" />
        </button>

        <div className="sm:grid sm:grid-cols-2">
          <div className="aspect-square w-full bg-surface flex items-center justify-center overflow-hidden">
            {box.photo ? (
              <SharpPhoto small={box.thumb} large={box.photo} alt={box.name} />
            ) : (
              <Gift className="w-8 h-8 text-line" />
            )}
          </div>
          <div className="p-6">
            <h2 className="font-display text-[22px] font-semibold text-ink leading-tight">
              {box.name}
            </h2>
            <p className="mt-2 text-[12.5px] text-muted2">
              Ready-made · one of a kind — once it's gone, it's gone
            </p>

            {box.price !== undefined && box.price !== null && (
              <div className="text-[22px] font-semibold text-ink mt-4">
                {money(box.price, sym)}
              </div>
            )}

            {box.lines?.length > 0 && (
              <div className="mt-5">
                <div className="eyebrow mb-2">What's inside</div>
                <div className="divide-y divide-line">
                  {box.lines.map((l, i) => (
                    <div key={`${l.name}-${i}`} className="flex items-center gap-3 py-2">
                      <span className="text-[13px] text-ink flex-1 truncate">{l.name}</span>
                      <span className="font-mono text-[12.5px] text-muted2 shrink-0">
                        ×{l.qty}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {link && (
              <a
                href={link}
                target="_blank"
                rel="noreferrer"
                className="mt-5 flex items-center justify-center gap-2 h-11 rounded-xl bg-stock-in text-white text-[13.5px] font-medium hover:opacity-90 transition-opacity"
              >
                <MessageCircle className="w-4 h-4" />
                Ask about this on WhatsApp
              </a>
            )}
            <p className="text-[11px] text-muted2 mt-2.5 leading-relaxed text-center">
              Nothing is paid here — the message opens in WhatsApp for you to send.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The item list or grid, however the client has it laid out — pulled out of
 * the page component so it can render the flat catalogue, a department's
 * filtered contents, or a box's eligible items, identically.
 */
function ItemGrid({ items, shop, sym, building, boxAllows, boxFull, basket, onOpenItem, onBox, onBasket }) {
  if (shop.layout === "list") {
    return (
      <div className="space-y-3">
        {items.map((item, idx) => (
          <button
            key={`${item.name}-${idx}`}
            data-large={item.photo || undefined}
            onClick={() => onOpenItem(item)}
            className={cn(
              "w-full text-left rounded-2xl border border-line bg-white overflow-hidden flex gap-4 p-3 hover:border-muted2 transition-colors",
              item.availability === "out" && "opacity-60"
            )}
          >
            <Thumb item={item} className="w-20 h-20 rounded-xl shrink-0" />
            <div className="min-w-0 flex-1 py-0.5">
              <div className="text-[14px] text-ink">{item.name}</div>
              {item.description && (
                <p className="text-[12.5px] text-muted2 mt-1 leading-relaxed line-clamp-2">
                  {item.description}
                </p>
              )}
              <div className="flex items-center gap-2 mt-2">
                {item.price !== undefined && (
                  <span className="font-semibold text-[14px] text-ink">
                    {money(item.price, sym)}
                  </span>
                )}
                <Availability item={item} />
              </div>
            </div>
          </button>
        ))}
      </div>
    );
  }

  return (
    // Cards keep one size whatever the count. With only a few items they
    // centre rather than stretching — stretching made a two-item shop look
    // like a billboard.
    <div
      className={cn("grid gap-4 grid-cols-2 lg:grid-cols-4", items.length < 4 && "lg:justify-center")}
      style={
        items.length < 4
          ? { gridTemplateColumns: `repeat(${items.length}, minmax(0, 240px))` }
          : undefined
      }
    >
      {items.map((item, idx) => (
        <div
          key={`${item.name}-${idx}`}
          data-large={item.photo || undefined}
          className={cn(
            "rounded-2xl border border-line bg-white overflow-hidden flex flex-col",
            item.availability === "out" && "opacity-60"
          )}
        >
          <button onClick={() => onOpenItem(item)} className="text-left">
            <Thumb item={item} className="aspect-square w-full" />
          </button>
          <div className="p-3.5 flex flex-col flex-1">
            <button
              onClick={() => onOpenItem(item)}
              className="text-left text-[13px] text-ink leading-snug min-h-[36px]"
            >
              {item.name}
            </button>
            <div className="flex items-center gap-2 mt-2">
              {item.price !== undefined && (
                <span className="font-semibold text-[14px] text-ink">
                  {money(item.price, sym)}
                </span>
              )}
              <Availability item={item} className="ml-auto" />
            </div>
            <AddControl
              item={item}
              shop={shop}
              building={building}
              boxAllows={boxAllows}
              boxFull={boxFull}
              inBox={!!building?.lines.find((l) => l.name === item.name)}
              qty={basket.find((b) => b.name === item.name)?.qty || 0}
              onBox={onBox}
              onBasket={onBasket}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Add to basket, or put in the box — the same control either way. */
/** One of whatever this client calls them, and the plural. */
function boxWords(shop) {
  const word = shop.box_word || "box";
  const words = shop.box_word_plural || "boxes";
  return { word, words, Word: word.charAt(0).toUpperCase() + word.slice(1) };
}

/** Add to the box being built, or to the basket — whichever applies. */
function AddControl({ item, shop, building, boxAllows, boxFull, inBox, qty, onBox, onBasket }) {
  const { word, Word } = boxWords(shop);
  if (item.availability === "out") return null;

  // While a box is open, eligible items go into it; anything else behaves as
  // normal, so a customer can still pick up a loose item mid-build.
  if (building && boxAllows(item)) {
    return inBox ? (
      <button
        onClick={() => onBox(item, -1)}
        className="mt-2.5 w-full rounded-lg py-2 text-[12px] font-medium bg-gold-light text-gold inline-flex items-center justify-center gap-1.5"
      >
        <Check className="w-3.5 h-3.5" /> In the {word}
      </button>
    ) : (
      <button
        onClick={() => onBox(item, 1)}
        disabled={boxFull}
        className="mt-2.5 w-full rounded-lg py-2 text-[12px] font-medium bg-gold-light text-gold disabled:opacity-40"
      >
        {boxFull ? `${Word} is full` : `Put in the ${word}`}
      </button>
    );
  }

  if (!shop.allow_cart) return null;

  if (qty > 0) {
    return (
      <div className="mt-2.5 flex items-center justify-between border border-line rounded-lg px-1 py-1">
        <button
          onClick={() => onBasket(item, -1)}
          aria-label="One fewer"
          className="w-7 h-7 rounded-md hover:bg-surface text-muted2"
        >
          −
        </button>
        <span className="text-[12px] text-ink">{qty}</span>
        <button
          onClick={() => onBasket(item, 1)}
          aria-label="One more"
          className="w-7 h-7 rounded-md hover:bg-surface text-muted2"
        >
          +
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => onBasket(item, 1)}
      className="mt-2.5 w-full rounded-lg py-2 text-[12px] font-medium transition-opacity hover:opacity-90"
      style={{ background: "color-mix(in srgb, var(--shop) 12%, white)", color: "var(--shop)" }}
    >
      Add to basket
    </button>
  );
}

/**
 * Boxes and basket in one panel, two tabs.
 *
 * Boxes are named so several can be built at once — one for Mum, one for Kay —
 * and any of them can be sent on its own or folded into the basket alongside
 * loose items.
 */
function BagPanel({
  side, onSide, shop, sym, boxes, basket, basketTotal, boxTotal, boxMin, boxMax,
  buildingId, onBuild, onRename, onDropBox, onBoxLineOut, onBoxToBasket,
  onBasketBoxOut, boxLink, basketLink, onAdd, onRemove, onClose,
}) {
  const { words } = boxWords(shop);
  const basketCount = basket.reduce((n, b) => n + b.qty, 0);
  const showTabs = shop.allow_box && shop.allow_cart;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button onClick={onClose} aria-label="Close" className="absolute inset-0 bg-ink/40" />
      <div className="relative bg-white w-full sm:max-w-[420px] h-full flex flex-col">
        <div className="px-5 py-4 border-b border-line flex items-center">
          <h2 className="font-display text-[17px] font-semibold text-ink">
            {side === "box" ? `Your ${words}` : "Your basket"}
          </h2>
          <button onClick={onClose} aria-label="Close" className="ml-auto p-1 text-muted2">
            <X className="w-4 h-4" />
          </button>
        </div>

        {showTabs && (
          <div className="flex gap-0.5 bg-surface m-4 mb-0 p-0.5 rounded-lg">
            {[
              { key: "box", label: `${words.charAt(0).toUpperCase() + words.slice(1)} · ${boxes.length}` },
              { key: "basket", label: `Basket · ${basketCount}` },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => onSide(t.key)}
                className={cn(
                  "flex-1 py-1.5 rounded-md text-[12px] transition-colors",
                  side === t.key ? "bg-white text-ink font-medium" : "text-muted2"
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {side === "box" ? (
            boxes.length === 0 ? (
              <p className="px-5 py-10 text-[13px] text-muted2 text-center">
                No {words} yet. Start one from the shop.
              </p>
            ) : (
              boxes.map((box) => (
                <BoxCard
                  key={box.id}
                  box={box}
                  sym={sym}
                  shop={shop}
                  boxMin={boxMin}
                  boxMax={boxMax}
                  total={boxTotal(box)}
                  building={buildingId === box.id}
                  onBuild={() => onBuild(buildingId === box.id ? null : box.id)}
                  onRename={(name) => onRename(box.id, name)}
                  onDrop={() => onDropBox(box.id)}
                  onLineOut={(line) => onBoxLineOut(box.id, line)}
                  onToBasket={() => {
                    onBoxToBasket(box);
                    onSide("basket");
                  }}
                  link={boxLink(box)}
                />
              ))
            )
          ) : basket.length === 0 ? (
            <p className="px-5 py-10 text-[13px] text-muted2 text-center">
              Nothing in the basket yet.
            </p>
          ) : (
            basket.map((b) =>
              b.kind === "box" ? (
                <div key={b.id} className="flex gap-3 px-5 py-3 border-b border-line items-center bg-gold-light/50">
                  <span className="w-11 h-11 rounded-lg bg-gold text-white flex items-center justify-center shrink-0">
                    <Package className="w-5 h-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] text-ink font-medium">{b.name}</div>
                    <div className="text-[11.5px] text-muted2 mt-0.5">
                      {b.lines.length} items
                    </div>
                  </div>
                  <span className="font-mono text-[12.5px] shrink-0">{money(b.price, sym)}</span>
                  <button
                    onClick={() => onBasketBoxOut(b)}
                    aria-label={`Take ${b.name} out`}
                    className="w-6 h-6 rounded text-muted2 hover:text-stock-out shrink-0"
                  >
                    <X className="w-3.5 h-3.5 mx-auto" />
                  </button>
                </div>
              ) : (
                <div key={b.name} className="flex gap-3 px-5 py-3 border-b border-line items-center">
                  <Thumb item={b} className="w-11 h-11 rounded-lg shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] text-ink leading-tight">{b.name}</div>
                    {b.price !== undefined && (
                      <div className="text-[11.5px] text-muted2 mt-0.5">
                        {b.qty} × {money(b.price, sym)}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => onAdd(b, -1)} aria-label="One fewer"
                      className="w-6 h-6 rounded text-muted2 hover:bg-surface">−</button>
                    <span className="text-[12px] w-4 text-center">{b.qty}</span>
                    <button onClick={() => onAdd(b, 1)} aria-label="One more"
                      className="w-6 h-6 rounded text-muted2 hover:bg-surface">+</button>
                    {/* Taking something out shouldn't mean tapping minus four times. */}
                    <button onClick={() => onRemove(b)} aria-label={`Remove ${b.name}`}
                      className="w-6 h-6 rounded text-muted2 hover:text-stock-out ml-0.5">
                      <X className="w-3.5 h-3.5 mx-auto" />
                    </button>
                  </div>
                </div>
              )
            )
          )}
        </div>

        {side === "basket" && basket.length > 0 && (
          <div className="border-t border-line px-5 py-4">
            <div className="flex items-baseline mb-3">
              <span className="text-[13.5px] font-medium text-ink flex-1">Total</span>
              <span className="font-mono text-[20px] font-semibold text-ink">
                {money(basketTotal, sym)}
              </span>
            </div>
            <a
              href={basketLink()}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 h-11 rounded-xl bg-stock-in text-white text-[13.5px] font-medium hover:opacity-90 transition-opacity"
            >
              <MessageCircle className="w-4 h-4" />
              Send as a WhatsApp order
            </a>
            <p className="text-[11.5px] text-muted2 mt-2.5 leading-relaxed text-center">
              Nothing is paid here — the message opens in WhatsApp for you to send.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/** One box: its name, what's in it, and what can be done with it. */
function BoxCard({ box, sym, shop, boxMin, boxMax, total, building, onBuild, onRename, onDrop, onLineOut, onToBasket, link }) {
  const short = box.lines.length < boxMin;

  return (
    <div className="border-b border-line">
      <div className="flex items-center gap-2 px-5 py-3 bg-gold-light/40 mt-0">
        <input
          value={box.name}
          onChange={(e) => onRename(e.target.value)}
          aria-label="Box name"
          className="flex-1 min-w-0 bg-transparent text-[13px] font-medium text-ink outline-none border-b border-transparent focus:border-gold"
        />
        <span className="text-[11.5px] text-muted2 shrink-0">
          {box.lines.length} of {boxLabel(boxMin, boxMax)} items
        </span>
        <button onClick={onDrop} aria-label={`Remove ${box.name}`}
          className="w-6 h-6 rounded text-muted2 hover:text-stock-out shrink-0">
          <X className="w-3.5 h-3.5 mx-auto" />
        </button>
      </div>

      {/* How full it is, without having to read the number. */}
      <div className="h-1.5 mx-5 mt-2 rounded-full bg-surface overflow-hidden">
        <span
          className="block h-full rounded-full bg-gold transition-all"
          style={{ width: `${Math.min(100, (box.lines.length / boxMin) * 100)}%` }}
        />
      </div>

      {box.lines.map((l) => (
        <div key={l.name} className="flex gap-3 px-5 py-2.5 items-center">
          <Thumb item={l} className="w-9 h-9 rounded-lg shrink-0" />
          <span className="text-[12.5px] flex-1 min-w-0 truncate">{l.name}</span>
          <button onClick={() => onLineOut(l)} aria-label={`Take out ${l.name}`}
            className="w-6 h-6 rounded text-muted2 hover:text-stock-out shrink-0">
            <X className="w-3.5 h-3.5 mx-auto" />
          </button>
        </div>
      ))}

      <div className="px-5 pt-2 pb-4">
        {shop.box_price > 0 && (
          <div className="flex items-baseline mb-2.5">
            <span className="text-[12.5px] text-muted2 flex-1">As a box</span>
            <span className="font-mono text-[15px] font-semibold text-ink">
              {money(total, sym)}
            </span>
          </div>
        )}

        {short ? (
          <p className="text-[12px] text-stock-low mb-2">
            {boxMin - box.lines.length} more to choose before it can be sent.
          </p>
        ) : (
          <a
            href={link}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 h-10 rounded-xl bg-stock-in text-white text-[13px] font-medium hover:opacity-90 mb-2"
          >
            <MessageCircle className="w-4 h-4" /> Send this box
          </a>
        )}

        <div className="flex gap-2">
          <button
            onClick={onBuild}
            className={cn(
              "flex-1 h-9 rounded-lg text-[12.5px] border",
              building ? "border-gold text-gold bg-gold-light" : "border-line text-ink"
            )}
          >
            {building ? "Building this one" : "Add more to it"}
          </button>
          {shop.allow_cart && !short && (
            <button
              onClick={onToBasket}
              className="flex-1 h-9 rounded-lg border border-line text-[12.5px] text-ink"
            >
              Add to basket
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function boxLabel(min, max) {
  return min === max ? `${min}` : `${min} to ${max}`;
}

/**
 * One control doing two jobs.
 *
 * Tapped, it drops the whole category list down to scroll. Typed into, it
 * narrows — categories first, since there are twenty of those against hundreds
 * of products, so a few letters are more likely aimed at one.
 */
function Finder({ shop, sym, query, onQuery, category, onCategory, onOpenItem }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => box.current && !box.current.contains(e.target) && setOpen(false);
    const key = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  const counts = useMemo(() => {
    const out = {};
    shop.items.forEach((i) => {
      out[i.category] = (out[i.category] || 0) + 1;
    });
    return out;
  }, [shop.items]);

  const q = query.trim().toLowerCase();

  const matchedCategories = useMemo(
    () => shop.categories.filter((c) => !q || c.toLowerCase().includes(q)),
    [shop.categories, q]
  );

  const matchedItems = useMemo(
    () => (q ? shop.items.filter((i) => i.name.toLowerCase().includes(q)).slice(0, 6) : []),
    [shop.items, q]
  );

  const nothing = q && matchedCategories.length === 0 && matchedItems.length === 0;

  return (
    <div className="mb-6">
      <div className="relative" ref={box}>
        <div
          className={cn(
            "flex items-center gap-2.5 px-3.5 h-11 rounded-xl border bg-white transition-colors",
            open ? "border-muted2/40" : "border-line"
          )}
        >
          <Search className="w-4 h-4 text-muted2 shrink-0" />
          <input
            value={query}
            onChange={(e) => {
              onQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={
              shop.categories.length > 1 ? "Search, or browse a category" : "Search"
            }
            className="flex-1 min-w-0 bg-transparent text-[13px] outline-none placeholder:text-muted2"
          />
          {query ? (
            <button onClick={() => onQuery("")} aria-label="Clear" className="text-muted2 shrink-0">
              <X className="w-4 h-4" />
            </button>
          ) : (
            shop.categories.length > 1 && (
              <button
                onClick={() => setOpen(!open)}
                aria-label="Browse categories"
                className="text-muted2 shrink-0"
              >
                <ChevronDown className={cn("w-4 h-4 transition-transform", open && "rotate-180")} />
              </button>
            )
          )}
        </div>

        {open && (
          <div className="absolute inset-x-0 mt-1.5 z-30 rounded-xl border border-line bg-white shadow-lift overflow-hidden">
            {nothing ? (
              <p className="px-4 py-6 text-[12.5px] text-muted2 text-center">
                Nothing matches that.
              </p>
            ) : (
              <div className="max-h-[300px] overflow-y-auto pb-1.5">
                {matchedCategories.length > 0 && (
                  <>
                    <div className="eyebrow px-4 pt-2.5 pb-1">Categories</div>
                    {!q && (
                      <button
                        onClick={() => {
                          onCategory("all");
                          setOpen(false);
                        }}
                        className={cn(
                          "w-full flex items-center px-4 py-2 text-[12.5px] text-left",
                          category === "all" ? "bg-brand-light text-brand font-medium" : "hover:bg-surface"
                        )}
                      >
                        All categories
                        <span className="ml-auto font-mono text-[11px] text-muted2">
                          {shop.items.length}
                        </span>
                      </button>
                    )}
                    {matchedCategories.map((c) => (
                      <button
                        key={c}
                        onClick={() => {
                          onCategory(c);
                          onQuery("");
                          setOpen(false);
                        }}
                        className={cn(
                          "w-full flex items-center px-4 py-2 text-[12.5px] text-left",
                          category === c ? "bg-brand-light text-brand font-medium" : "hover:bg-surface"
                        )}
                      >
                        <span className="truncate">{c}</span>
                        <span className="ml-auto font-mono text-[11px] text-muted2 shrink-0 pl-2">
                          {counts[c] || 0}
                        </span>
                      </button>
                    ))}
                  </>
                )}

                {matchedItems.length > 0 && (
                  <>
                    <div className="eyebrow px-4 pt-3 pb-1 border-t border-line mt-1.5">
                      Products
                    </div>
                    {matchedItems.map((item, i) => (
                      <button
                        key={`${item.name}-${i}`}
                        onClick={() => {
                          // Already found it — open it rather than filtering to it.
                          onOpenItem(item);
                          setOpen(false);
                        }}
                        className="w-full flex items-center gap-2.5 px-4 py-2 text-left hover:bg-surface"
                      >
                        <Thumb item={item} className="w-7 h-7 rounded-md shrink-0" />
                        <span className="text-[12.5px] truncate">{item.name}</span>
                        {item.price !== undefined && (
                          <span className="ml-auto font-mono text-[11px] text-muted2 shrink-0 pl-2">
                            {money(item.price, sym)}
                          </span>
                        )}
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* The box goes back to being a search box, so the chosen category needs
          somewhere of its own to sit. */}
      {category !== "all" && (
        <button
          onClick={() => onCategory("all")}
          className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand-light text-brand px-3.5 py-1.5 text-[12px] hover:opacity-80 transition-opacity"
        >
          {category} · {counts[category] || 0} item{(counts[category] || 0) === 1 ? "" : "s"}
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

const prefetched = new Set();

/** Start downloading a photo before it's needed, once. */
function prefetchPhoto(url) {
  if (!url || prefetched.has(url)) return;
  prefetched.add(url);
  const img = new Image();
  img.decoding = "async";
  img.src = url;
}

/**
 * A photo opened up. The small copy — already downloaded for the grid — shows
 * at once, and the bigger one fades in over it when it arrives, so the space
 * is never empty.
 */
function SharpPhoto({ small, large, alt }) {
  const [ready, setReady] = useState(false);
  const layered = small && large && small !== large;
  return (
    <div className="relative w-full h-full">
      {layered && (
        <img src={small} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
      )}
      <img
        src={large || small}
        alt={alt}
        decoding="async"
        onLoad={() => setReady(true)}
        className={cn(
          "relative w-full h-full object-cover transition-opacity duration-300",
          layered && !ready ? "opacity-0" : "opacity-100"
        )}
      />
    </div>
  );
}

/** An item's photo. Lists and cards get the small copy; `large` (an item opened
 *  up) gets the bigger one, with the small copy standing in while it loads. */
function Thumb({ item, className, large = false }) {
  return (
    <div className={cn("bg-surface flex items-center justify-center overflow-hidden", className)}>
      {item.photo && large ? (
        <SharpPhoto small={item.thumb} large={item.photo} alt={item.name} />
      ) : item.photo ? (
        <img
          src={item.thumb || item.photo}
          alt={item.name}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover"
        />
      ) : (
        // A plain initial beats a broken image icon.
        <span className="font-display text-[24px] text-line select-none">
          {item.name[0]?.toUpperCase()}
        </span>
      )}
    </div>
  );
}

function Availability({ item, className }) {
  if (!item.availability) return null;
  const out = item.availability === "out";
  return (
    <span
      className={cn(
        "text-[11px] shrink-0",
        out ? "text-stock-out" : item.availability === "low" ? "text-stock-low" : "text-stock-in",
        className
      )}
    >
      {out ? "Sold out" : item.availability === "low" ? "Only a few left" : "Available"}
    </span>
  );
}

/** A closer look, and the message written ready to send. */
function ItemSheet({ item, sym, wa, askAbout, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    const unlock = lockPageScroll();
    return () => {
      window.removeEventListener("keydown", onKey);
      unlock();
    };
  }, [onClose]);

  const out = item.availability === "out";

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <button onClick={onClose} aria-label="Close" className="absolute inset-0 bg-ink/40" />
      <div className="relative bg-white w-full sm:max-w-[720px] sm:rounded-2xl rounded-t-2xl overflow-hidden max-h-[92vh] overflow-y-auto">
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 z-10 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center"
        >
          <X className="w-4 h-4 text-ink" />
        </button>

        <div className="sm:grid sm:grid-cols-2">
          <Thumb item={item} large className="aspect-square w-full" />
          <div className="p-6">
            <h2 className="font-display text-[22px] font-semibold text-ink leading-tight">
              {item.name}
            </h2>
            <div className="flex items-center gap-2.5 mt-2 text-[12.5px] text-muted2">
              {item.category && <span>{item.category}</span>}
              {item.category && item.availability && <span>·</span>}
              <Availability item={item} />
            </div>

            {item.price !== undefined && (
              <div className="text-[22px] font-semibold text-ink mt-4">
                {money(item.price, sym)}
              </div>
            )}

            {item.description && (
              <p className="text-[13px] text-muted2 mt-3 leading-relaxed whitespace-pre-line">
                {item.description}
              </p>
            )}

            {wa && (
              <a
                href={askAbout(item)}
                target="_blank"
                rel="noreferrer"
                className="mt-5 flex items-center justify-center gap-2 h-11 rounded-xl bg-stock-in text-white text-[13.5px] font-medium hover:opacity-90 transition-opacity"
              >
                <MessageCircle className="w-4 h-4" />
                {out ? "Ask when it's back" : "Ask about this on WhatsApp"}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
